import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { StrikerBrand } from '@striker/ui-shell';

type Block = { id: string; label: string; category: string };

export function PublicSosPage() {
  const [params] = useSearchParams();
  const token = String(params.get('token') || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [session, setSession] = useState<{
    email: string;
    business_name: string | null;
    critical_blocks: Block[];
  } | null>(null);
  const [blockId, setBlockId] = useState('login');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Link SOS mancante. Richiedine uno nuovo dalla pagina di accesso.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/v1/public/support/sos/validate?token=${encodeURIComponent(token)}`,
        );
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
          email?: string;
          business_name?: string | null;
          critical_blocks?: Block[];
        };
        if (!res.ok) {
          throw new Error(body.message || `Link non valido (${res.status})`);
        }
        if (!cancelled) {
          setSession({
            email: String(body.email || ''),
            business_name: body.business_name || null,
            critical_blocks: body.critical_blocks || [],
          });
          if (body.critical_blocks?.[0]?.id) setBlockId(body.critical_blocks[0].id);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/public/support/sos/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          critical_block: blockId,
          contact_phone: phone,
          message,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) throw new Error(body.message || `Invio fallito (${res.status})`);
      setOk(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sk-login">
      <div className="sk-login__card" style={{ maxWidth: 480 }}>
        <div className="sk-login__brand">
          <StrikerBrand surface="dark" title="StrikerPos" />
          <span className="sk-muted" style={{ marginTop: '0.35rem', display: 'block' }}>
            SOS Assistenza
          </span>
        </div>
        {ok ? (
          <>
            <h1 className="sk-page__title">Segnalazione inviata</h1>
            <p className="sk-page__lead">
              Il team master ha ricevuto la tua emergenza. Ti contatteremo al numero indicato.
            </p>
            <Link className="sk-btn sk-btn--block" to="/">
              Torna al login
            </Link>
          </>
        ) : (
          <>
            <h1 className="sk-page__title">Sblocco emergenza</h1>
            <p className="sk-page__lead">
              {session
                ? `${session.business_name || 'Locale'} · ${session.email}`
                : 'Validazione link…'}
            </p>
            {error ? <p className="sk-error">{error}</p> : null}
            {session ? (
              <form onSubmit={onSubmit}>
                <fieldset className="sk-field">
                  <legend>Blocco critico</legend>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {session.critical_blocks.map((block) => (
                      <label key={block.id} style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="block"
                          checked={blockId === block.id}
                          onChange={() => setBlockId(block.id)}
                        />
                        <span>{block.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className="sk-field">
                  <span>Cellulare</span>
                  <input
                    className="sk-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </label>
                <label className="sk-field">
                  <span>Dettagli</span>
                  <textarea
                    className="sk-input"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </label>
                <button type="submit" className="sk-btn sk-btn--block" disabled={busy}>
                  {busy ? 'Invio…' : 'Invia SOS'}
                </button>
              </form>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
